import { useState } from "react";
import SEO from "@/components/SEO";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import RobotPageNav from "@/components/RobotPageNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FinanceApplicationForm } from "@/components/FinanceApplicationForm";
import sh1Img from "@/assets/robots/sh1-main.png";

const SH1 = () => {
  const [financeOpen, setFinanceOpen] = useState(false);
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background/95 to-background">
      <SEO 
        title="PUDU SH1 - Smart Upright Scrubber Dryer | Professional Floor Cleaning"
        description="Professional smart scrubber with 27kg brush pressure, 20000pa suction, and 9 cleaning modes. Reduces cleaning time by 70% and water usage by 80%. Multidimensional cleaning capability."
        keywords="PUDU SH1, scrubber dryer, floor scrubber, commercial cleaning, upright scrubber, smart cleaning, professional cleaning equipment"
      />
      <Navigation />
      
      <main className="container mx-auto px-4 pt-44 pb-16 md:pt-48">
        <RobotPageNav />
        
        {/* Hero Section */}
        <section className="text-center mb-16 animate-fade-in">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-orbitron font-bold mb-6 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent leading-tight break-words">
            PUDU SH1
          </h1>
          <p className="text-base md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            Smart Upright Scrubber Dryer - Professional Floor Cleaning Solution
          </p>
          
          <div className="relative max-w-4xl mx-auto mb-8">
            <img 
              src={sh1Img} 
              alt="PUDU SH1 Smart Upright Scrubber Dryer" 
              className="w-full h-auto object-contain rounded-xl shadow-2xl"
            />
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="glow-gold">
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                Request Demo
              </a>
            </Button>
            <Button size="lg" variant="outline" onClick={() => setFinanceOpen(true)}>
              Apply for Financing
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="tel:3369624539">
                Call: (336) 962-4539
              </a>
            </Button>
          </div>
        </section>

        {/* Key Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl font-bold text-primary mb-2">27kg</div>
            <div className="text-sm text-muted-foreground">Brush Pressure</div>
          </Card>
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl font-bold text-primary mb-2">20000pa</div>
            <div className="text-sm text-muted-foreground">Suction Power</div>
          </Card>
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl font-bold text-primary mb-2">70%</div>
            <div className="text-sm text-muted-foreground">Time Reduction</div>
          </Card>
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl font-bold text-primary mb-2">80%</div>
            <div className="text-sm text-muted-foreground">Water Savings</div>
          </Card>
        </section>

        {/* Tabs */}
        <Tabs defaultValue="features" className="mb-16">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="features">Features</TabsTrigger>
            <TabsTrigger value="specs">Specifications</TabsTrigger>
            <TabsTrigger value="modes">Cleaning Modes</TabsTrigger>
          </TabsList>

          <TabsContent value="features" className="space-y-8">
            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Advanced Cleaning Capabilities</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-xl mb-3">High-Pressure Deep Cleaning</h3>
                  <p className="text-muted-foreground">
                    27kg brush pressure combined with 350rpm brush speed delivers powerful high-pressure cleaning that dissolves even the toughest stains.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3">Instant Scrub-and-Dry</h3>
                  <p className="text-muted-foreground">
                    20000pa suction power provides instant scrub-and-dry capability, leaving floors spotless and ready to use immediately.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3">Air-Liquid-Debris Separation</h3>
                  <p className="text-muted-foreground">
                    Advanced separation system prevents drain blockages and simplifies waste disposal for easier maintenance.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3">Multidimensional Cleaning</h3>
                  <p className="text-muted-foreground">
                    Cleans vertical surfaces, dead zones, low-profile spaces, and provides 360° coverage for comprehensive facility cleaning.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Smart Features</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">Touch Screen Interface</h3>
                  <p className="text-muted-foreground">
                    Intuitive touch screen provides component monitoring, consumable tracking, and on-screen training for operators.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">Modular Quick-Release Design</h3>
                  <p className="text-muted-foreground">
                    Easy maintenance system allows quick replacement of brushes, squeegees, tanks, and batteries without tools.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">High Productivity</h3>
                  <p className="text-muted-foreground">
                    1100-1600 m²/h cleaning productivity with 44cm working width ensures efficient coverage of large areas.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="specs">
            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Technical Specifications</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Brush Pressure</h3>
                    <p className="text-muted-foreground">27 kg</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Brush Speed</h3>
                    <p className="text-muted-foreground">350 rpm</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Suction Power</h3>
                    <p className="text-muted-foreground">20,000 pa</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Working Width</h3>
                    <p className="text-muted-foreground">44 cm</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Tank Capacity</h3>
                    <p className="text-muted-foreground">4 L (Clean & Dirty Water)</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Productivity</h3>
                    <p className="text-muted-foreground">1,100 - 1,600 m²/h</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Runtime</h3>
                    <p className="text-muted-foreground">70 - 100 minutes</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Interface</h3>
                    <p className="text-muted-foreground">Touch Screen Display</p>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="modes">
            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">9 Versatile Cleaning Modes</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="border border-primary/20 rounded-lg p-4">
                  <h3 className="font-bold mb-2">Normal Mode</h3>
                  <p className="text-sm text-muted-foreground">Standard daily cleaning operations</p>
                </div>
                <div className="border border-primary/20 rounded-lg p-4">
                  <h3 className="font-bold mb-2">Heavy Duty</h3>
                  <p className="text-sm text-muted-foreground">Intensive cleaning for tough stains</p>
                </div>
                <div className="border border-primary/20 rounded-lg p-4">
                  <h3 className="font-bold mb-2">ECO Mode</h3>
                  <p className="text-sm text-muted-foreground">Energy and water efficient operation</p>
                </div>
                <div className="border border-primary/20 rounded-lg p-4">
                  <h3 className="font-bold mb-2">Low Residue</h3>
                  <p className="text-sm text-muted-foreground">Minimal chemical usage for sensitive areas</p>
                </div>
                <div className="border border-primary/20 rounded-lg p-4">
                  <h3 className="font-bold mb-2">Soak Mode</h3>
                  <p className="text-sm text-muted-foreground">Pre-treatment for stubborn stains</p>
                </div>
                <div className="border border-primary/20 rounded-lg p-4">
                  <h3 className="font-bold mb-2">Accessory Mode</h3>
                  <p className="text-sm text-muted-foreground">Specialized cleaning attachments</p>
                </div>
                <div className="border border-primary/20 rounded-lg p-4">
                  <h3 className="font-bold mb-2">Water Suction</h3>
                  <p className="text-sm text-muted-foreground">Liquid removal and spill cleanup</p>
                </div>
                <div className="border border-primary/20 rounded-lg p-4">
                  <h3 className="font-bold mb-2">Brush Max</h3>
                  <p className="text-sm text-muted-foreground">Maximum brush power for deep cleaning</p>
                </div>
                <div className="border border-primary/20 rounded-lg p-4">
                  <h3 className="font-bold mb-2">Custom</h3>
                  <p className="text-sm text-muted-foreground">User-configurable settings</p>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Benefits Section */}
        <section className="mb-16">
          <Card className="p-8 glass-effect">
            <h2 className="text-2xl font-bold mb-6 text-primary text-center">Key Benefits</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-2">70%</div>
                <div className="text-xl font-semibold mb-2">Time Reduction</div>
                <p className="text-muted-foreground">
                  Dramatically reduce cleaning time compared to traditional methods
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-2">80%</div>
                <div className="text-xl font-semibold mb-2">Water Savings</div>
                <p className="text-muted-foreground">
                  Environmentally friendly operation with significant water conservation
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <Card className="p-12 glass-effect border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Transform Your Floor Cleaning Operations</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Experience the power of professional smart scrubbing with the PUDU SH1. Reduce time, save water, and achieve superior cleaning results.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg" className="glow-gold">
                <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                  Schedule Demo
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="tel:3369624539">
                  Call: (336) 962-4539
                </a>
              </Button>
            </div>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SH1;
